// src/components/testpage/WidgetLoaderControls.jsx
import React from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Select, MenuItem, FormControl, InputLabel, Button,
  RadioGroup, FormControlLabel, Radio, Divider, Tooltip, IconButton
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { toast } from 'react-toastify'; // For the copy ID toast

// Placeholder for IdDisplayWithCopy if not using a separate component
// In a real setup, you'd import this: import IdDisplayWithCopy from '../common/IdDisplayWithCopy';
const IdDisplayWithCopy = ({ id, label }) => (
  id ? (
    <Typography variant="caption" display="block" sx={{ display: 'flex', alignItems: 'center', wordBreak: 'break-all' }}>
      <strong style={{ marginRight: '4px' }}>{label}:</strong> {id}
      <Tooltip title="Copy ID">
        <IconButton 
          size="small" 
          onClick={() => { navigator.clipboard.writeText(id); toast.success(`${label} copied!`); }}
          sx={{ ml: 0.5 }}
        >
          <InfoIcon fontSize="inherit" /> {/* Using InfoIcon as a placeholder for a copy icon */}
        </IconButton>
      </Tooltip>
    </Typography>
  ) : null
);


const WidgetLoaderControls = ({
  // State props
  loadMode,
  selectedVersionId,
  selectedTemplateId,
  allWidgetTemplates,
  allWidgetVersions,
  loadingTemplates,
  loadingVersions,
  checkingWidget,
  widgetDetected,
  widgetLoadError,
  resolvedVersionId,
  resolvedAgentId,
  savingToDB, // To disable controls during DB save

  // Callback props
  onLoadModeChange,
  onSelectedVersionChange,
  onSelectedTemplateChange,
  onFetchAndSetLiveConfigForVersion, // Callback to load DB config to panel

  // Constants
  widgetGlobalObjectName = 'banty', // Pass WIDGET_GLOBAL_OBJECT as a prop
  theme, // Pass theme for styling if needed, e.g., border colors
}) => {

  const canLoadWidget = !!(loadMode === 'version' ? selectedVersionId : selectedTemplateId);

  return (
    <Paper elevation={2} sx={{ p: { xs: 2, sm: 2.5 }, mb: 3, position: 'sticky', top: '80px' /* Adjust as needed */ }}>
      <Typography variant="h5" component="h2" gutterBottom fontWeight="medium">
        Widget Loader
      </Typography>
      
      <FormControl component="fieldset" sx={{mb:2}} disabled={checkingWidget || savingToDB}>
        <RadioGroup 
          row 
          aria-label="load-mode" 
          name="load-mode" 
          value={loadMode} 
          onChange={(e) => onLoadModeChange(e.target.value)}
        >
          <FormControlLabel value="version" control={<Radio />} label="Load by Version ID" />
          <FormControlLabel value="template" control={<Radio />} label="Load by Template ID" />
        </RadioGroup>
      </FormControl>

      {loadMode === 'version' && (
        <FormControl fullWidth margin="normal" disabled={loadingVersions || savingToDB || checkingWidget}>
          <InputLabel id="widget-version-select-label">Select Specific Widget Version</InputLabel>
          <Select 
            labelId="widget-version-select-label" 
            value={selectedVersionId} 
            label="Select Specific Widget Version" 
            onChange={(e) => onSelectedVersionChange(e.target.value)}
          >
            {loadingVersions && <MenuItem value=""><CircularProgress size={20} sx={{mr:1}}/><em>Loading versions...</em></MenuItem>}
            {!loadingVersions && <MenuItem value="" disabled><em>{allWidgetVersions.length === 0 ? "No versions found" : "Select a version"}</em></MenuItem>}
            {allWidgetVersions.map((v) => ( 
              <MenuItem key={v.id} value={v.id}>
                {v.displayName || `${v.name || 'Unnamed Version'} (Template: ${v.template?.name || 'N/A'})`} (ID: ...{v.id.slice(-6)})
              </MenuItem> 
            ))}
          </Select>
        </FormControl>
      )}

      {loadMode === 'template' && (
        <FormControl fullWidth margin="normal" disabled={loadingTemplates || savingToDB || checkingWidget}>
          <InputLabel id="widget-template-select-label">Select Widget Template (Loads Active Version)</InputLabel>
          <Select 
            labelId="widget-template-select-label" 
            value={selectedTemplateId} 
            label="Select Widget Template (Loads Active Version)" 
            onChange={(e) => onSelectedTemplateChange(e.target.value)}
          >
            {loadingTemplates && <MenuItem value=""><CircularProgress size={20} sx={{mr:1}}/><em>Loading templates...</em></MenuItem>}
            {!loadingTemplates && <MenuItem value="" disabled><em>{allWidgetTemplates.length === 0 ? "No templates found" : "Select a template"}</em></MenuItem>}
            {allWidgetTemplates.map((t) => ( 
              <MenuItem key={t.id} value={t.id}>
                {t.name} (ID: ...{t.id.slice(-6)})
              </MenuItem> 
            ))}
          </Select>
        </FormControl>
      )}

      <Divider sx={{my:2}}/>
      <Typography variant="h6" component="h3" gutterBottom>
        Status & Info
      </Typography>
      <Alert 
        severity={ !canLoadWidget ? "info" : checkingWidget ? "info" : widgetDetected ? "success" : "warning" } 
        sx={{mb: 1.5, '& .MuiAlert-message': {width: '100%'}}}
        icon={checkingWidget ? <CircularProgress size={20} color="inherit"/> : (widgetDetected ? undefined : <InfoIcon/>)}
      >
        {!canLoadWidget ? "Select a target above to load the widget." : 
         checkingWidget ? "Loading / Detecting Widget..." : 
         widgetDetected ? `Widget (${widgetGlobalObjectName}) is live and ready.` : 
         widgetLoadError ? `Load Error: ${widgetLoadError.substring(0,100)}...` : `Widget (${widgetGlobalObjectName}) not detected.`
        }
      </Alert>
      
      {widgetLoadError && !checkingWidget && (
        <Typography variant="caption" color="error" display="block" sx={{mb:1.5}}>
          {widgetLoadError}
        </Typography>
      )}

      {resolvedVersionId && (
        <Box sx={{mb: 1.5, p: 1.5, border: `1px solid ${theme?.palette?.divider || '#e0e0e0'}`, borderRadius: 1, background: theme?.palette?.action?.hover || '#f5f5f5' }}>
          <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
            Resolved Loaded Instance:
          </Typography>
          <IdDisplayWithCopy id={resolvedVersionId} label="Loaded Version ID" />
          {resolvedAgentId && <IdDisplayWithCopy id={resolvedAgentId} label="Linked Agent ID" />}
          
          { (loadMode === 'version' && selectedVersionId && resolvedVersionId && selectedVersionId !== resolvedVersionId) &&
            <Alert severity="warning" variant="outlined" sx={{fontSize: '0.8rem', mt:1, p:0.5}}>
              Selected version ({selectedVersionId.slice(-4)}) differs from resolved ({resolvedVersionId.slice(-4)}). This is unexpected.
            </Alert>
          }
           <Button 
                size="small" 
                variant="text" 
                onClick={() => onFetchAndSetLiveConfigForVersion(resolvedVersionId)} 
                sx={{mt:1, fontSize: '0.75rem', lineHeight: 1.2, p: '2px 8px'}} // Adjusted padding
                disabled={!resolvedVersionId || savingToDB || checkingWidget}
            >
                Load this version's full DB config to panel
            </Button>
        </Box>
      )}

      { (loadMode === 'template' && selectedTemplateId && allWidgetVersions.length > 0 && !resolvedVersionId && widgetDetected) &&
          <Alert severity="info" variant="outlined" sx={{fontSize: '0.8rem', mt:1, p:0.5}}>
              Template loaded. If an active version exists, its ID will show above once widget fully initializes its internal config.
          </Alert>
      }
    </Paper>
  );
};

export default WidgetLoaderControls;